// @ts-nocheck
import React from 'react'
import { getFinalType, deriveRowStatus, isDIR, resolvePrevType } from '../targetingUtils'
import styles from '../css/KpiBar.module.css'

function AbcRow({ types, values, label }) {
    return (
        <div className={styles.abcRow}>
            {types.map(t => (
                <span key={t} className={[styles.abcChip, styles[`type${t}`]].join(' ')}>
                    {t} {values[t] ?? 0}
                </span>
            ))}
            {label && <span className={styles.abcLabel}>{label}</span>}
        </div>
    )
}

function Tile({ title, value, valueClass, borderClass, typeLabel, abcTypes, abcValues, subLines }) {
    return (
        <div className={[styles.tile, borderClass].join(' ')}>
            <div className={styles.tileTitle}>{title}</div>
            <div className={[styles.tileValue, valueClass].join(' ')}>{value}</div>
            {typeLabel && <div className={styles.typeLabel}>{typeLabel}</div>}
            {abcTypes && <AbcRow types={abcTypes} values={abcValues} />}
            {subLines && subLines.map((line, i) => (
                <div key={i} className={styles.subLine}>{line}</div>
            ))}
        </div>
    )
}

function countByType(rowList, getType) {
    const counts = {}
    rowList.forEach(r => {
        const t = getType(r)
        if (t && t !== 'NT') counts[t] = (counts[t] || 0) + 1
    })
    return counts
}

export default function KpiBar({ status, rows = [], allowDIR = false, prevCol = 'PREV_TARGET_TYPE', prevFallbackCol = null, visibleTypes = ['A', 'B', 'C'] }) {
    if (!status) return null

    const ABC = visibleTypes

    // Exclude DIR rows from all KPI totals — they don't count against the targeting universe
    const activeRows = allowDIR ? rows.filter(r => !isDIR(r)) : rows

    // --- Categorise every row by derived status (pure from row data) ---
    const addedRows     = activeRows.filter(r => deriveRowStatus(r, prevCol, prevFallbackCol) === 'added')
    const droppedRows   = activeRows.filter(r => deriveRowStatus(r, prevCol, prevFallbackCol) === 'dropped')
    const promotedRows  = activeRows.filter(r => deriveRowStatus(r, prevCol, prevFallbackCol) === 'promoted')
    const demotedRows   = activeRows.filter(r => deriveRowStatus(r, prevCol, prevFallbackCol) === 'demoted')
    const unchangedRows = activeRows.filter(r => deriveRowStatus(r, prevCol, prevFallbackCol) === 'unchanged')
    const retainedRows  = [...promotedRows, ...demotedRows, ...unchangedRows]

    // --- Tile totals ---
    const prevRows       = activeRows.filter(r => { const p = resolvePrevType(r, prevCol, prevFallbackCol); return p && p !== 'NT' })
    const prevTotal      = prevRows.length
    const addedCount   = addedRows.length
    const droppedCount = droppedRows.length
    // Retained / final counts must match the ABC chip logic: only rows with a real (non-NT) final type
    const retainedTotal = retainedRows.filter(r => { const finalType = getFinalType(r); return finalType && finalType !== 'NT' }).length
    const finalTotal    = addedRows.filter(r => { const finalType = getFinalType(r); return finalType && finalType !== 'NT' }).length
                        + retainedTotal

    // --- ABC breakdowns (each tile uses the correct type column per spec) ---
    // prevRows tile  → previous type
    const prevAbc      = countByType(prevRows,      r => resolvePrevType(r, prevCol, prevFallbackCol))
    // Added tile → final type of newly added rows
    const addedAbc   = countByType(addedRows,   r => getFinalType(r))
    // Dropped tile → previous type (last known) of dropped rows
    const droppedAbc = countByType(droppedRows, r => resolvePrevType(r, prevCol, prevFallbackCol))
    // Retained tile → final type of retained rows
    const retainedAbc = countByType(retainedRows, r => getFinalType(r))
    // Final tile → final type of all rows that still have a target
    const finalAbc      = countByType([...addedRows, ...retainedRows], r => getFinalType(r))

    const promotedCount  = promotedRows.length
    const demotedCount   = demotedRows.length
    // Filter NT-NT rows so subline counts are consistent with retainedTotal
    const unchangedCount = unchangedRows.filter(r => { const finalType = getFinalType(r); return finalType && finalType !== 'NT' }).length

    return (
        <div className={styles.bar}>
            <Tile
                title="Current Targets"
                value={prevTotal}
                borderClass={styles.borderNeutral}
                typeLabel="Current"
                abcTypes={ABC}
                abcValues={prevAbc}
            />
            <Tile
                title="Newly added"
                value={addedCount > 0 ? `+${addedCount}` : addedCount}
                valueClass={styles.valueGreen}
                borderClass={styles.borderGreen}
                typeLabel="Final"
                abcTypes={ABC}
                abcValues={addedAbc}
            />
            <Tile
                title="Dropped"
                value={droppedCount > 0 ? `\u2212${droppedCount}` : droppedCount}
                valueClass={styles.valueRed}
                borderClass={styles.borderRed}
                typeLabel="Final"
                abcTypes={ABC}
                abcValues={droppedAbc}
            />
            <Tile
                title="Retained"
                value={retainedTotal}
                borderClass={styles.borderNeutral}
                typeLabel="Final"
                abcTypes={ABC}
                abcValues={retainedAbc}
                subLines={[
                    `↑ ${promotedCount} promoted`,
                    `↓ ${demotedCount} demoted`,
                    `= ${unchangedCount} unchanged`,
                ]}
            />
            <Tile
                title="Final total"
                value={finalTotal}
                borderClass={styles.borderBlue}
                typeLabel="Final"
                abcTypes={ABC}
                abcValues={finalAbc}
            />
        </div>
    )
}
